
import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator"
import { deep_clone } from "../global.functions"
import { VMenu } from "vuetify/lib/components"
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CountdownInterface } from "../store/config.types";
import HelpComponent from "./help.vue";

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)

@Component({
  components: {
    "help": HelpComponent
  }
})
export default class HeaderCountdownConfigEditComponent extends Vue {
  contextHelp = "## Countdown menu\nYou can add countdowns to be shown in the header (up to three enabled at once). You can pull a list of predefined countdowns or create your own. If you want to pause (hold) a countdown you can just double-click on it in the header."
  @Prop()
  value!: boolean;
  @Prop()
  initialData!: CountdownInterface;
  show: boolean = false;
  countdown: CountdownInterface = {
    enabled: false,
    title: "",
    description: "",
    remainingSeconds: 0
  };
  pickerTab: number = 0;
  targetDatePicker = true;
  targetTimePicker = false;
  targetTimestampMenu: boolean = false;
  targetTimezone = "GMT";
  targetTimezones = [
    {'value': 'GMT', 'text': 'GMT'},
    {'value': dayjs.tz.guess(), 'text': 'Local'},
    {'value': 'America/Chicago', 'text': 'MCC Houston'},
    {'value': 'America/New_York', 'text': 'Washington DC'},
    {'value': 'Europe/Moscow', 'text': 'MCC Moscow'}
  ]
  valid: boolean = true

  get enabled() {
    return this.countdown.enabled
  }
  set enabled(value: boolean) {
    Vue.set(this.countdown, "enabled", value)
  } 
  get title() {
    return this.countdown.title || ""
  }
  set title(value: string) {
    Vue.set(this.countdown, "title", value.slice(0, 10))
  }
  get description() {
    return typeof this.countdown.description === "function" ? "" : this.countdown.description
  }
  set description(value: string) {
    Vue.set(this.countdown, "description", value)
  }
  titleRules: Array<(v: string) => boolean | string> = [
    v => v.length <= 10 || 'Max. 10 characters'
  ]

  get tab() {
    return this.countdown.remainingSeconds !== undefined ? 1 : 0;
  }
  set tab(value: number) {
    let time = dayjs.utc().set('milliseconds', 0)
    if (value == 0) {
      Vue.set(this.countdown, "timestamp", time.subtract(this.countdown.remainingSeconds || 0, "milliseconds").toISOString())
      Vue.set(this.countdown, "remainingSeconds", undefined)
    } else {
      Vue.set(this.countdown, "remainingSeconds", time.diff(dayjs.utc(this.countdown.timestamp)))
      Vue.set(this.countdown, "timestamp", undefined)
    }
  }

  get targetTimestamp() {
    return dayjs.utc(new Date(this.countdown.timestamp || Date.now())).tz(this.targetTimezone).format("DD.MM.YYYY HH:mm:ss")
  }
  set targetTimestamp(value: string) {
    try {
      let ts = dayjs.tz(value, "DD.MM.YYYY HH:mm:ss", this.targetTimezone);
      if (ts.toString() !== "Invalid Date") {
        ts.millisecond(0);
        this.countdown.timestamp = ts.toISOString()
      }
    } catch (e) {}
  }
  targetTimestampRules: Array<(v: string) => boolean | string> = [
    v => {
      let result = false
      try {
        result = dayjs.utc(v, "DD.MM.YYYY HH:mm:ss", true).toString() !== "Invalid Date"
      } catch (e) {}
      return result || "Invalid format (e.g. 13.06.2024 20:01:59)"
    }
  ]

  get countdownTimestamp() {
    return this.countdown.timestamp || dayjs.utc().toISOString()
  }
  get targetDate() {
    return this.countdownTimestamp.split("T")[0]
  }
  set targetDate(value: string) {
    Vue.set(this.countdown, "timestamp", `${value}T${this.countdownTimestamp.split("T")[1]}`)
  }
  get targetTime() {
    return this.countdownTimestamp.split("T")[1].split(".")[0]
  }
  set targetTime(value: string) {
    Vue.set(this.countdown, "timestamp", `${this.countdownTimestamp.split("T")[0]}T${value}.000Z`)
  }

  get remainingSeconds() {
    let value = dayjs.duration(Math.abs(this.countdown.remainingSeconds || 0))
    return `${(this.countdown.remainingSeconds || 0) <= 0 ? '-' : '+'}${(value.asDays() | 0).toString().padStart(3, "0")}:${value.format("HH:mm:ss")}`;
  }
  set remainingSeconds(value: string) {
    let parsed = value.match(/^(?<dir>[\-+])(?<days>\d{3}):(?<hours>\d{2}):(?<minutes>\d{2}):(?<seconds>\d{2})$/)
    if (parsed?.groups) {
      Vue.set(this.countdown, "remainingSeconds", dayjs.duration({
        days: (parsed.groups.dir == '-' ? -1 : 1 ) * parseInt(parsed.groups.days),
        hours: (parsed.groups.dir == '-' ? -1 : 1 ) * parseInt(parsed.groups.hours),
        minutes: (parsed.groups.dir == '-' ? -1 : 1 ) * parseInt(parsed.groups.minutes),
        seconds: (parsed.groups.dir == '-' ? -1 : 1 ) * parseInt(parsed.groups.seconds),
      }).asMilliseconds())
    }
  }
  remainingSecondsRules: Array<(v: string) => boolean | string> = [
    v => v.match(/^[\-+]\d{3}:\d{2}:\d{2}:\d{2}$/) != null || "Invalid format (e.g. -000:00:04:00)"
  ]

  exit(save?: boolean) {
    if (save) {
      (this.$refs["form"] as HTMLFormElement).validate()
      if (!this.valid) {
        return
      }
      this.$emit('save', this.countdown)
    }
    this.show = false
  }

  @Watch('value')
  watchValue(value: boolean) {
    this.show = value;
  }
  @Watch('show')
  watchShow(value: boolean) {
    if (value) {
      this.countdown = this.initialData
      this.targetTimezone = "GMT"
    }

    this.$emit('input', value)
  }
}
